import { Box, Grid, Grid2, IconButton, InputAdornment } from "@mui/material"
import { Autocomplete, CurrencyInput, Input, Loader, Modal, NumericInput } from "../../../components"
import { useFormik } from "formik"
import { Add } from "@mui/icons-material"
import { useMilespayAPI, useToggle } from "../../../hooks"
import { AddCustomerModal } from "../AddCustomerModal"
import { company_customer, company_gateway } from '@milespay.co/db'
import { GATEWAY_PROVIDER_OPTIONS } from '@milespay.co/utils'
import { enqueueSnackbar } from "notistack"
import { CreateOrderSchema } from "@milespay.co/validations"

interface CreateOrderModalProps {
    onClose: () => void
    onSuccess: (orderId: string) => void
}

const INSTALLMENTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

export const CreateOrderModal = ({ onClose, onSuccess }: CreateOrderModalProps) => {
    const [addCustomerModalVisibility, addCustomerModalVisibilityControls] = useToggle(false)
    
    const [companyCustomers, companyCustomersControls] = useMilespayAPI({
        url: '/company-customer'
    })

    const [companyGateways, companyGatewaysControls] = useMilespayAPI({
        url: '/company-gateway'
    })

    const [createOrder, createOrderControls] = useMilespayAPI({
        url: '/company-order',
        method: 'POST'
    }, {
        manual: true
    })

    const formik = useFormik({
        initialValues: {
            company_customer_id: '',
            price: 0,
            company_gateway_id: '',
            installments: 1,
            locator: '',
        },
        validationSchema: CreateOrderSchema,
        onSubmit: async (values) => {
            const response = await createOrderControls({
                data: {
                    ...values
                }
            })

            if (response.status >= 200) {
                enqueueSnackbar('Pedido criado com sucesso!', {variant: 'success'})
                onSuccess(response.data.id)
            }
        }
    })

    const companyCustomerOptions = (companyCustomers.data || []).map((item: company_customer) => ({
        label: item.name,
        value: item.id
    }))

    const installmentsOptions = (INSTALLMENTS).map((item: number) => ({
        label: `${item}x`,
        value: item
    }))

    console.log('companyGateways', companyGateways.data)
    const companyGatewayOptions = (companyGateways.data || []).map((item: company_gateway) => ({
        label: GATEWAY_PROVIDER_OPTIONS.find(option => option.value === item.gateway_provider)?.label,
        value: item.id
    }))
    
    return (
        <>
            <Modal
                title="Adicionar Pedido"
                open
                onClose={createOrder.loading ? undefined : onClose}
                bodyCustomCss={{
                    width: 500
                }}
                onConfirm={createOrder.loading ? undefined : formik.submitForm}
                confirmLabel="Adicionar"
            >
                <Box>
                    {createOrder.loading ? (
                        <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
                            <Loader />
                        </Box>
                    ) : (
                        <Grid2 container spacing={1}>
                        <Grid2 size={{xs: 12}}>
                            <Input
                                label="Localizador"
                                name="locator"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={!!formik.touched.locator && !!formik.errors.locator}
                                helperText={formik.touched.locator && formik.errors.locator}
                                value={formik.values.locator}
                            />
                        </Grid2>
                        <Grid2 size={{xs: 12}}>
                            <Input 
                                label="Cliente"
                                name="company_customer_id"
                                slotProps={{
                                    input: {
                                        startAdornment: <InputAdornment position="end">
                                        <IconButton
                                        onClick={addCustomerModalVisibilityControls.setTrue}
                                        edge="start"
                                        >
                                        <Add />
                                        </IconButton>
                                    </InputAdornment>
                                    }
                                }}
                                options={companyCustomerOptions}
                                select
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={!!formik.touched.company_customer_id && !!formik.errors.company_customer_id}
                                helperText={formik.touched.company_customer_id && formik.errors.company_customer_id}
                                value={formik.values.company_customer_id}
                            />
                        </Grid2>
                        <Grid2 size={{xs: 12}}>
                            <Input 
                                select
                                name="installments"
                                label="Parcelas Permitidas"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.installments}
                                error={!!formik.touched.installments && !!formik.errors.installments}
                                helperText={formik.touched.installments && formik.errors.installments}
                                options={installmentsOptions}
                            />
                        </Grid2>
                        <Grid2 size={{xs: 12}}>
                            <CurrencyInput 
                                label="Valor (R$)"
                                onChangeValue={(_, originaValue) => {
                                    formik.setFieldValue('price', originaValue)
                                }}
                                onBlur={formik.handleBlur}
                                value={formik.values.price}
                                error={!!formik.touched.price && !!formik.errors.price}
                                helperText={formik.touched.price && formik.errors.price}
                            />
                        </Grid2>
                        <Grid2 size={{xs: 12}}>
                            <Input 
                                label="Gateway"
                                name="company_gateway_id"
                                options={companyGatewayOptions}
                                select
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={!!formik.touched.company_gateway_id && !!formik.errors.company_gateway_id}
                                helperText={formik.touched.company_gateway_id && formik.errors.company_gateway_id}
                                value={formik.values.company_gateway_id}
                            />
                        </Grid2>
                    </Grid2>
                    )}
                </Box>
            </Modal>
            {
                addCustomerModalVisibility && (
                    <AddCustomerModal 
                        onClose={addCustomerModalVisibilityControls.setFalse}
                        onSuccess={(customerId: string) => {
                            addCustomerModalVisibilityControls.setFalse()

                            formik.setFieldValue('company_customer_id', customerId)

                            companyCustomersControls()
                        }}
                    />
                )
            }
        </>
    )
}